import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Stack,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  alpha,
  useTheme,
  Pagination,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Visibility,
  Refresh,
  Storage,
  Person,
  LocationOn,
  Terrain,
  PhotoCamera,
  Inventory,
  Numbers,
  Edit,
  Delete,
  Search,
  FilterList,
  Water,
  Landscape,
  Warning,
  ExpandMore,
  DateRange,
  Work,
  Public,
  Category,
  Straighten,
  WbSunny,
  Opacity,
  EditNote,
  Agriculture,
  TrendingUp,
  Layers,
  FilterHdr,
  Texture,
  Calculate,
  MyLocation,
  Grass,
  NaturePeople,
  ArrowUpward,
  ArrowDownward,
} from "@mui/icons-material";
import api, { base_URL } from "../../api";
import { useNavigate } from "react-router-dom";
import LegendCard from "../../components/cards/LegendCard";
import AkkFormulaCard from "../../components/cards/AkkFormulaCard";
import StorieIndexCard from "../../components/cards/StorieIndexCard";
import RecalculateAllButton from "../../components/admin/RecalculateAllButton";
import DataTableFrame from "../../components/common/DataTableFrame";
import PageShell, { PanelCard } from "../../components/common/PageShell";
import {
  profilDuvariResmi,
  horizonResimleri,
  cevreResimleri,
} from "../../utils/photoUtils";

const DataItem = ({ label, value, icon, color = "primary.main" }) => (
  <Box sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0", height: "100%" }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        fontWeight: "bold",
        textTransform: "uppercase",
        fontFamily: "Montserrat",
        fontSize: "0.65rem",
      }}
    >
      {icon ? React.cloneElement(icon, { sx: { fontSize: 14, color } }) : null}{" "}
      {label.replace(/_/g, " ")}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        mt: 0.5,
        fontWeight: 600,
        color: "text.primary",
        wordBreak: "break-word",
      }}
    >
      {Array.isArray(value)
        ? value.join(", ")
        : typeof value === "object"
          ? JSON.stringify(value)
          : value || "-"}
    </Typography>
  </Box>
);

const customAlpha = (color, opacity) => {
  if (color.startsWith("#")) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  return color;
};

const SortableHeader = ({ label, field, sortBy, sortDir, onSort }) => {
  const active = sortBy === field;
  return (
    <TableCell
      sx={{
        fontWeight: 700,
        fontFamily: "Montserrat",
        cursor: "pointer",
        userSelect: "none",
        whiteSpace: "nowrap",
        bgcolor: "#F8F9FA",
      }}
      onClick={() => onSort(field)}
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <span>{label}</span>
        {active ? (
          sortDir === "asc" ? (
            <ArrowUpward sx={{ fontSize: 14 }} />
          ) : (
            <ArrowDownward sx={{ fontSize: 14 }} />
          )
        ) : (
          <ArrowUpward sx={{ fontSize: 14, opacity: 0.2 }} />
        )}
      </Stack>
    </TableCell>
  );
};

const ROWS_PER_PAGE = 50;

const SORT_OPTIONS = [
  { value: "profil_no", label: "Profil No" },
  { value: "created_at", label: "Yüklenme Tarihi" },
  { value: "tarih", label: "Arazi Tarihi" },
  { value: "agent_name", label: "Personel" },
  { value: "akk_sonuc", label: "AKK Sonucu" },
  { value: "ilce_mahalle", label: "Konum" },
  { value: "job_name", label: "Çalışma Adı" },
];

export default function ProfileListPage() {
  const user = JSON.parse(localStorage.getItem("fieldUser") || "{}");
  const isAuditor = user?.role === "auditor";

  const navigate = useNavigate();
  const theme = useTheme();
  const API_URL = base_URL;

  const [profiles, setProfiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [loading, setLoading] = useState(false);

  const [selectedProfile, setSelectedProfile] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState("profil_no");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/all-data", {
        params: {
          page,
          limit: ROWS_PER_PAGE,
          search: debouncedSearch,
          sortBy,
          sortDir,
        },
      });
      if (res.data.success) {
        setProfiles(res.data.data);
        setTotal(res.data.total);
        setPageCount(res.data.pageCount);
      }
    } catch (err) {
      console.error("Veri çekme hatası:", err);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, sortBy, sortDir]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const handleSort = (field) => {
    if (sortBy === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(field);
      setSortDir("asc");
    }
    setPage(1);
  };

  const handleOpenDetail = (row) => {
    let parsedData = {};
    try {
      parsedData =
        typeof row.full_data === "string"
          ? JSON.parse(row.full_data)
          : row.full_data;
    } catch (e) {
      parsedData = {};
    }
    setSelectedProfile({ ...row, details: parsedData });
    setOpenModal(true);
  };

  const handleDelete = async (id, profil_no) => {
    if (
      !window.confirm(
        `"${profil_no}" profilini silmek istediğinize emin misiniz?\nBu işlem geri alınamaz.`,
      )
    )
      return;
    try {
      await api.delete(`/admin/profile/${id}`);
      fetchProfiles();
    } catch (err) {
      alert(
        "Silme işlemi başarısız: " +
          (err.response?.data?.message || err.message),
      );
    }
  };

  const getImageSrc = (img) => {
    if (!img) return "";
    return img.startsWith("data:image") ? img : `${API_URL}${img}`;
  };

  return (
    <PageShell
      title="Profil Arşivi"
      icon={<Storage />}
      chip={
        <Chip
          label={`${total} Kayıt`}
          size="small"
          color="secondary"
          sx={{ fontWeight: "bold" }}
        />
      }
      actions={
        <>
          {/* Tum profillerin AKK / Storie indekslerini yeniden hesaplar.
              Bileşen zaten vardı ama sayfaya hiç yerleştirilmemişti. */}
          <RecalculateAllButton onComplete={fetchProfiles} />
          <Button
            startIcon={<Refresh />}
            variant="contained"
            color="primary"
            onClick={fetchProfiles}
          >
            Yenile
          </Button>
        </>
      }
    >
      {/* FİLTRELEME & SIRALAMA */}
      <PanelCard>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr auto auto" },
            gap: 2,
            alignItems: "center",
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Profil No, Personel, Konum veya AKK Ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Sırala</InputLabel>
            <Select
              value={sortBy}
              label="Sırala"
              onChange={(e) => {
                setSortBy(e.target.value);
                setPage(1);
              }}
              sx={{ borderRadius: 2 }}
            >
              {SORT_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setSortDir((d) => (d === "asc" ? "desc" : "asc"));
              setPage(1);
            }}
            startIcon={sortDir === "asc" ? <ArrowUpward /> : <ArrowDownward />}
            sx={{ borderRadius: 2, whiteSpace: "nowrap", height: 40 }}
          >
            {sortDir === "asc" ? "Artan" : "Azalan"}
          </Button>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}
        >
          <FilterList fontSize="small" />
          {loading
            ? "Yükleniyor..."
            : `Toplamda ${total} kayıt — bu sayfada ${profiles.length} gösteriliyor.`}
        </Typography>
      </PanelCard>

      {/* TABLO */}
      <DataTableFrame minWidth={1400}>
          <TableHead>
            <TableRow>
              <SortableHeader
                label="Profil No"
                field="profil_no"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortableHeader
                label="Çuval No"
                field="cuval_no"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortableHeader
                label="Personel"
                field="agent_name"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortableHeader
                label="Çalışma Adı"
                field="job_name"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortableHeader
                label="Konum"
                field="ilce_mahalle"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortableHeader
                label="AKK"
                field="akk_sonuc"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <TableCell
                sx={{
                  fontWeight: 700,
                  fontFamily: "Montserrat",
                  bgcolor: "#F8F9FA",
                }}
              >
                Medya
              </TableCell>
              <SortableHeader
                label="Tarih"
                field="created_at"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <TableCell
                align="center"
                sx={{
                  fontWeight: 700,
                  fontFamily: "Montserrat",
                  bgcolor: "#F8F9FA",
                }}
              >
                İşlemler
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : profiles.length > 0 ? (
              profiles.map((row) => {
                // Horizon ve Çevre resimlerini analiz ediyoruz
                let details = {};
                try {
                  details =
                    typeof row.full_data === "string"
                      ? JSON.parse(row.full_data)
                      : row.full_data || {};
                } catch (e) {
                  details = {};
                }

                // Profil duvari (tek) + horizon ve cevre fotograf sayilari
                const hasDuvar = !!profilDuvariResmi(details);
                const horizonPhotoCount = horizonResimleri(details).length;
                const envPhotoCount = cevreResimleri(details).length;

                return (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography fontWeight="bold" color="primary.dark">
                        {row.profil_no}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.cuval_no || "Yok"}
                        size="small"
                        variant="outlined"
                        color="secondary"
                        icon={<Inventory sx={{ fontSize: 14 }} />}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {row.agent_name || row.full_name}
                    </TableCell>
                    <TableCell>{row.job_name}</TableCell>
                    <TableCell>{row.ilce_mahalle || "-"}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={row.akk_sonuc || "-"}
                        color="primary"
                        size="small"
                        sx={{ fontWeight: "bold" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Tooltip
                          title={
                            hasDuvar
                              ? "Profil Duvarı Fotoğrafı Var"
                              : "Profil Duvarı Fotoğrafı Yok"
                          }
                        >
                          <Chip
                            size="small"
                            icon={<FilterHdr fontSize="small" />}
                            label={hasDuvar ? "Var" : "Yok"}
                            color={hasDuvar ? "success" : "error"}
                            variant={hasDuvar ? "filled" : "outlined"}
                            sx={{ height: 24, fontSize: "0.7rem" }}
                          />
                        </Tooltip>
                        <Tooltip
                          title={`Horizon Fotoğrafı: ${horizonPhotoCount} adet`}
                        >
                          <Chip
                            size="small"
                            icon={<Landscape fontSize="small" />}
                            label={horizonPhotoCount}
                            color={horizonPhotoCount > 0 ? "warning" : "default"}
                            variant={
                              horizonPhotoCount > 0 ? "filled" : "outlined"
                            }
                            sx={{ height: 24, fontSize: "0.7rem" }}
                          />
                        </Tooltip>
                        <Tooltip
                          title={`Çevre Fotoğrafı: ${envPhotoCount} adet`}
                        >
                          <Chip
                            size="small"
                            icon={<PhotoCamera fontSize="small" />}
                            label={envPhotoCount}
                            color={envPhotoCount > 0 ? "info" : "default"}
                            variant={envPhotoCount > 0 ? "filled" : "outlined"}
                            sx={{ height: 24, fontSize: "0.7rem" }}
                          />
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {new Date(row.created_at).toLocaleDateString("tr-TR")}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Görüntüle">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenDetail(row)}
                          sx={{
                            bgcolor: customAlpha(
                              theme.palette.primary.main,
                              0.1,
                            ),
                            mr: 0.5,
                          }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!isAuditor && (
                        <>
                          <Tooltip title="Düzenle">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() =>
                                navigate(`/admin/edit-profile/${row.id}`)
                              }
                              sx={{
                                bgcolor: customAlpha(
                                  theme.palette.secondary.main,
                                  0.1,
                                ),
                                mr: 0.5,
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Sil">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                handleDelete(row.id, row.profil_no)
                              }
                              sx={{
                                bgcolor: customAlpha(
                                  theme.palette.error.main,
                                  0.1,
                                ),
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                  <Typography
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    Kayıt bulunamadı.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
      </DataTableFrame>

      {/* SAYFALAMA */}
      {total > 0 && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 2, flexWrap: "wrap", gap: 1 }}
        >
          <Typography variant="caption" color="text.secondary">
            Sayfa {page} / {pageCount} — Gösterilen:{" "}
            {(page - 1) * ROWS_PER_PAGE + 1}–
            {Math.min(page * ROWS_PER_PAGE, total)} / {total}
          </Typography>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, v) => setPage(v)}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
          />
        </Stack>
      )}

      {/* DETAY MODAL */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            bgcolor: "primary.main",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
          }}
        >
          <Box sx={{ fontWeight: 700 }}>
            Teknik Profil Raporu: {selectedProfile?.profil_no}
          </Box>
          <Stack direction="row" spacing={1}>
            {selectedProfile?.cuval_no && (
              <Chip
                label={`Çuval: ${selectedProfile.cuval_no}`}
                sx={{
                  bgcolor: "secondary.dark",
                  color: "white",
                  fontWeight: "bold",
                }}
              />
            )}
            <IconButton
              onClick={() => setOpenModal(false)}
              sx={{ color: "white" }}
            >
              <ExpandMore sx={{ transform: "rotate(180deg)" }} />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: "#fcfcfd" }}>
          {selectedProfile && (
            <Box sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" },
                  gap: 3,
                  mb: 3,
                }}
              >
                {profilDuvariResmi(selectedProfile.details) && (
                  <Paper
                    elevation={0}
                    sx={{ p: 2, borderRadius: 3, border: "1px solid #e0e0e0" }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="primary"
                      sx={{
                        mb: 1,
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Landscape fontSize="small" /> PROFİL DUVARI
                    </Typography>
                    <Box
                      component="img"
                      src={getImageSrc(
                        profilDuvariResmi(selectedProfile.details),
                      )}
                      sx={{
                        width: "100%",
                        height: 250,
                        borderRadius: 2,
                        objectFit: "contain",
                        bgcolor: "#f9f9f9",
                        cursor: "zoom-in",
                      }}
                      onClick={() =>
                        window.open(
                          getImageSrc(
                            profilDuvariResmi(selectedProfile.details),
                          ),
                          "_blank",
                        )
                      }
                    />
                  </Paper>
                )}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                  }}
                >
                  {/* Once horizon, sonra cevre fotograflari */}
                  {[
                    ...horizonResimleri(selectedProfile.details).map(
                      (src, i) => ({ src, ad: `HORİZON ${i + 1}` }),
                    ),
                    ...cevreResimleri(selectedProfile.details).map(
                      (src, i) => ({ src, ad: `ÇEVRE ${i + 1}` }),
                    ),
                  ].map(
                    ({ src: imgUrl, ad }, index) => (
                      <Paper
                        key={index}
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: 3,
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          color="secondary"
                          sx={{
                            mb: 1,
                            fontWeight: 700,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            fontSize: "0.8rem",
                          }}
                        >
                          <PhotoCamera fontSize="small" /> {ad}
                        </Typography>
                        <Box
                          component="img"
                          src={getImageSrc(imgUrl)}
                          sx={{
                            width: "100%",
                            height: 160,
                            borderRadius: 2,
                            objectFit: "cover",
                            cursor: "zoom-in",
                            bgcolor: "#f5f5f5",
                          }}
                          onClick={() =>
                            window.open(getImageSrc(imgUrl), "_blank")
                          }
                        />
                      </Paper>
                    ),
                  )}
                </Box>
              </Box>
              <Stack spacing={3} sx={{ mb: 3 }}>
                <LegendCard formData={selectedProfile.details} />
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 3,
                  }}
                >
                  <AkkFormulaCard formData={selectedProfile.details} />
                  <StorieIndexCard formData={selectedProfile.details} />
                </Box>
              </Stack>
              {[
                {
                  title: "1. KİMLİK VE KONUM BİLGİLERİ",
                  color: "#1565c0",
                  data: [
                    {
                      label: "Çalışma Adı",
                      value:
                        selectedProfile.job_name ||
                        selectedProfile.details.calismanin_adi,
                      icon: <Work fontSize="inherit" />,
                    },
                    {
                      label: "Profil No",
                      value: selectedProfile.profil_no,
                      icon: <Numbers fontSize="inherit" />,
                    },
                    {
                      label: "Çuval No",
                      value: selectedProfile.details.cuval_no,
                      icon: <Texture fontSize="inherit" />,
                    },
                    {
                      label: "Tanımlayan",
                      value: selectedProfile.details.tanimlayan,
                      icon: <Person fontSize="inherit" />,
                    },
                    {
                      label: "Tarih",
                      value:
                        selectedProfile.details.tarih ||
                        selectedProfile.details.timestamp,
                      icon: <DateRange fontSize="inherit" />,
                    },
                    {
                      label: "İl",
                      value: selectedProfile.details.il,
                      icon: <Public fontSize="inherit" />,
                    },
                    {
                      label: "İlçe",
                      value: selectedProfile.details.ilce,
                      icon: <LocationOn fontSize="inherit" />,
                    },
                    {
                      label: "Mahalle",
                      value: selectedProfile.details.mahalle,
                      icon: <LocationOn fontSize="inherit" />,
                    },
                    {
                      label: "Profil Grubu",
                      value: selectedProfile.details.profilgrup,
                      icon: <Category fontSize="inherit" />,
                    },
                    {
                      label: "Koordinat X",
                      value: selectedProfile.details.koordinat_x,
                      icon: <MyLocation fontSize="inherit" />,
                    },
                    {
                      label: "Koordinat Y",
                      value: selectedProfile.details.koordinat_y,
                      icon: <MyLocation fontSize="inherit" />,
                    },
                    {
                      label: "Rakım (Z)",
                      value: selectedProfile.details.yukseklik
                        ? `${selectedProfile.details.yukseklik} m`
                        : "-",
                      icon: <Terrain fontSize="inherit" />,
                    },
                  ],
                },
                {
                  title: "2. ARAZİ VE TOPRAK ÖZELLİKLERİ",
                  color: "#795548",
                  data: [
                    {
                      label: "AKK Sınıfı",
                      value:
                        selectedProfile.details.akk_class_result ||
                        selectedProfile.akk_sonuc,
                      icon: <Calculate fontSize="inherit" />,
                    },
                    {
                      label: "Toprak Serisi",
                      value: selectedProfile.details.toprak_serisi,
                      icon: <Landscape fontSize="inherit" />,
                    },
                    {
                      label: "Toprak Seri Kodu",
                      value: selectedProfile.details.seri_kodu,
                      icon: <Numbers fontSize="inherit" />,
                    },
                    {
                      label: "İklim Tipi",
                      value: selectedProfile.details.iklim,
                      icon: <WbSunny fontSize="inherit" />,
                    },
                    {
                      label: "Ortalama Yağış",
                      value: selectedProfile.details.ort_yagis,
                      icon: <Opacity fontSize="inherit" />,
                    },
                    {
                      label: "Ortalama Sıcaklık",
                      value: selectedProfile.details.ort_sicaklik,
                      icon: <WbSunny fontSize="inherit" />,
                    },
                    {
                      label: "Ana Materyal",
                      value: selectedProfile.details.ana_materyal,
                      icon: <Terrain fontSize="inherit" />,
                    },
                    {
                      label: "Büyük Toprak Grubu (BTG)",
                      value: selectedProfile.details.btg,
                      icon: <Layers fontSize="inherit" />,
                    },
                    {
                      label: "Fizyografya",
                      value: selectedProfile.details.fizyografya,
                      icon: <FilterHdr fontSize="inherit" />,
                    },
                    {
                      label: "Kaya Tipi",
                      value: selectedProfile.details.kaya_tipi,
                      icon: <Terrain fontSize="inherit" />,
                    },
                    {
                      label: "Mikrorölyef",
                      value: selectedProfile.details.mikrorolyef,
                      icon: <Landscape fontSize="inherit" />,
                    },
                    {
                      label: "Yüzey Topoğrafyası",
                      value: selectedProfile.details.yuzey_topografyasi,
                      icon: <Landscape fontSize="inherit" />,
                    },
                    {
                      label: "Arazi Kullanım Sınıfı",
                      value: selectedProfile.details.arazi_kullanim_sinif,
                      icon: <Agriculture fontSize="inherit" />,
                    },
                    {
                      label: "Arazi Kullanım Detay",
                      value: selectedProfile.details.arazi_kullanim_detay,
                      icon: <Agriculture fontSize="inherit" />,
                    },
                    {
                      label: "Mevcut Ürün",
                      value: selectedProfile.details.urun,
                      icon: <Grass fontSize="inherit" />,
                    },
                    {
                      label: "Tesirli Derinlik",
                      value: selectedProfile.details.tesirli_derinlik,
                      icon: <Straighten fontSize="inherit" />,
                    },
                    {
                      label: "Kök Gel. Derinlik",
                      value: selectedProfile.details.kok_gel_derinlik,
                      icon: <Straighten fontSize="inherit" />,
                    },
                    {
                      label: "Kök Gel. Engel Faktör",
                      value: selectedProfile.details.kok_gel_eng_faktor,
                      icon: <Warning fontSize="inherit" />,
                    },
                    {
                      label: "Eğim (%)",
                      value: selectedProfile.details.egim_yuzde,
                      icon: <TrendingUp fontSize="inherit" />,
                    },
                    {
                      label: "Eğim Yönü",
                      value: selectedProfile.details.egim_yonu,
                      icon: <TrendingUp fontSize="inherit" />,
                    },
                    {
                      label: "Doğal Drenaj",
                      value: selectedProfile.details.tabii_drenaj,
                      icon: <Water fontSize="inherit" />,
                    },
                    {
                      label: "Taban Suyu Max",
                      value: selectedProfile.details.taban_suyu_max,
                      icon: <Water fontSize="inherit" />,
                    },
                    {
                      label: "Taban Suyu Min",
                      value: selectedProfile.details.taban_suyu_min,
                      icon: <Water fontSize="inherit" />,
                    },
                    {
                      label: "Taban Suyu Belirtileri",
                      value: selectedProfile.details.taban_suyu_belirti,
                      icon: <Water fontSize="inherit" />,
                    },
                    {
                      label: "Erozyon Derecesi",
                      value:
                        selectedProfile.details.su_erozyon_derecesi?.charAt(
                          0,
                        ) || "1",
                      icon: <Warning fontSize="inherit" />,
                    },
                    {
                      label: "Erozyon Şekli",
                      value: Array.isArray(
                        selectedProfile.details.erozyon_sekli,
                      )
                        ? selectedProfile.details.erozyon_sekli.join(", ")
                        : selectedProfile.details.erozyon_sekli,
                      icon: <Warning fontSize="inherit" />,
                    },
                    {
                      label: "Rüzgar Aşındırma",
                      value: selectedProfile.details.ruzgar_asinma,
                      icon: <Warning fontSize="inherit" />,
                    },
                    {
                      label: "Rüzgar Yığılma",
                      value: selectedProfile.details.ruzgar_yigilma,
                      icon: <Warning fontSize="inherit" />,
                    },
                    {
                      label: "İnsan Etkisi",
                      value: selectedProfile.details.insan_etkisi,
                      icon: <NaturePeople fontSize="inherit" />,
                    },
                    {
                      label: "Yüzey Çatlağı",
                      value: selectedProfile.details.yuzey_catlak,
                      icon: <Terrain fontSize="inherit" />,
                    },
                    {
                      label: "Taşkın Olasılığı",
                      value: selectedProfile.details.taskin_olasiligi,
                      icon: <Warning fontSize="inherit" />,
                    },
                    {
                      label: "Sel Basması",
                      value: selectedProfile.details.sel_basmasi,
                      icon: <Water fontSize="inherit" />,
                    },
                    {
                      label: "Yüzey Taşlılık",
                      value: selectedProfile.details.taslilik,
                      icon: <Terrain fontSize="inherit" />,
                    },
                    {
                      label: "Kayalılık",
                      value: selectedProfile.details.kayalilik,
                      icon: <Terrain fontSize="inherit" />,
                    },
                    {
                      label: "Çakıllılık",
                      value: selectedProfile.details.cakillilik,
                      icon: <Terrain fontSize="inherit" />,
                    },
                  ],
                },
              ].map((section, idx) => (
                <Paper
                  key={idx}
                  sx={{
                    p: 2,
                    mb: 3,
                    borderRadius: 3,
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: section.color,
                      fontWeight: 700,
                      mb: 2,
                      fontFamily: "Montserrat",
                    }}
                  >
                    {section.title}
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "1fr 1fr",
                        md: "repeat(4, 1fr)",
                      },
                      gap: 1,
                    }}
                  >
                    {section.data.map((item, i) => (
                      <DataItem
                        key={i}
                        label={item.label}
                        value={item.value}
                        icon={item.icon}
                      />
                    ))}
                  </Box>
                </Paper>
              ))}
              <Paper
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: 3,
                  border: "1px solid #e0e0e0",
                  overflow: "hidden",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "primary.dark",
                    fontWeight: 700,
                    mb: 2,
                    fontFamily: "Montserrat",
                  }}
                >
                  KATMAN (HORİZON) ANALİZLERİ
                </Typography>
                <DataTableFrame
                  size="small"
                  minWidth={650}
                  maxHeight={420}
                  sx={{ borderRadius: 2 }}
                >
                    <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                      <TableRow>
                        {[
                          "Horizon & Sınır",
                          "Renk (Munsell)",
                          "Tekstür & Yapısal",
                          "Kimyasal",
                          "Strüktür",
                          "Kıvam",
                          "Kökler",
                          "Gözlem",
                        ].map((h) => (
                          <TableCell key={h} sx={{ fontWeight: "bold" }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedProfile.details.horizons?.map((h, i) => (
                        <TableRow key={i} hover>
                          <TableCell valign="top">
                            <Typography
                              variant="subtitle2"
                              color="primary.main"
                            >
                              {h.sembol || "-"}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {h.derinlik_min} - {h.derinlik_max} cm{" "}
                              {h.derinlik_arti ? "+" : ""}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                            >
                              Sınır: {h.sinir_belirginlik} / {h.sinir_sekil}
                            </Typography>
                          </TableCell>
                          <TableCell valign="top">
                            <Typography variant="caption" display="block">
                              <b>KURU:</b> {h.renk_kod_kuru} (
                              {h.renk_sinif_kuru})
                            </Typography>
                            <Typography variant="caption" display="block">
                              <b>YAŞ:</b> {h.renk_kod_yas} ({h.renk_sinif_yas})
                            </Typography>
                          </TableCell>
                          <TableCell valign="top">
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              sx={{ color: "#2e7d32" }}
                            >
                              {h.tekstur}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Taşlılık: {h.taslilik || "-"}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Kireç: {h.kirec || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell valign="top">
                            <Typography variant="caption" display="block">
                              Alkalilik: {h.alkalilik || "-"}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Tuzluluk: {h.tuzluluk || "-"}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Bor: {h.bor || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell valign="top">
                            <Typography variant="caption" display="block">
                              {Array.isArray(h.struktur_tip)
                                ? h.struktur_tip.join(", ")
                                : h.struktur_tip}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                            >
                              Büy: {h.struktur_buyukluk} / Day:{" "}
                              {h.struktur_dayaniklilik}
                            </Typography>
                          </TableCell>
                          <TableCell valign="top">
                            <Typography variant="caption" display="block">
                              K: {h.kivam_kuru} / N: {h.kivam_nemli}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                            >
                              Y.Yap: {h.kivam_yas_yapiskanlik} / Y.Plast:{" "}
                              {h.kivam_yas_plastiklik}
                            </Typography>
                          </TableCell>
                          <TableCell valign="top">
                            <Typography variant="caption" display="block">
                              <b>SAÇAK:</b> {h.kok_sacak_miktar} (
                              {h.kok_sacak_kalinlik})
                            </Typography>
                            <Typography variant="caption" display="block">
                              <b>KAZIK:</b> {h.kok_kazik_miktar} (
                              {h.kok_kazik_kalinlik})
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 200 }} valign="top">
                            <Typography
                              variant="caption"
                              sx={{ fontStyle: "italic" }}
                            >
                              {h.ozel_gorunum}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                </DataTableFrame>
              </Paper>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: customAlpha(theme.palette.info.main, 0.05),
                  border: "1px dashed",
                  borderColor: "info.main",
                  borderRadius: 3,
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="info.main"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    fontFamily: "Montserrat",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <EditNote /> NOTLAR VE DÜŞÜNCELER
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontStyle: "italic", color: "text.primary" }}
                >
                  {selectedProfile.details.arazi_aciklama}{" "}
                  {selectedProfile.details.dusunceler || "Not bulunmamaktadır."}
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{ p: 2, bgcolor: "#f8f9fa", borderTop: "1px solid #e0e0e0" }}
        >
          <Button
            color="inherit"
            sx={{ fontWeight: 700 }}
            onClick={() => setOpenModal(false)}
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
